import React from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { QueryResultDto } from '../../../../../../../typescript-axios';
import { BarChartParameters } from '../config';

interface BarChartVisualizationProps {
    data: QueryResultDto;
    parameters?: BarChartParameters;
}

export function BarChartVisualization({ data, parameters }: BarChartVisualizationProps) {
    const xAxis = typeof parameters?.x_axis === 'string' ? parameters.x_axis : null;
    const yAxes = Array.isArray(parameters?.y_axes) ? (parameters.y_axes as string[]) : [];
    const showLegend = parameters?.show_legend === true;
    const groupSeriesBy = typeof parameters?.group_series_by === 'string' ? parameters.group_series_by : null;
    const layout = parameters?.layout === 'horizontal' ? 'horizontal' : 'vertical';
    const stacked = parameters?.stacked === true;

    if (!xAxis) return <div className="flex align-items-center justify-content-center h-full text-500">Missing x_axis in parameters</div>;
    if (yAxes.length === 0) return <div className="flex align-items-center justify-content-center h-full text-500">Missing y_axes in parameters</div>;
    if ((data?.rows ?? []).length === 0) return <div className="flex align-items-center justify-content-center h-full text-500">No data</div>;

    // ── Build series ──────────────────────────────────────────────────
    let series: { label: string; data: (number | null)[]; stack?: string }[];
    let xLabels: string[];

    if (groupSeriesBy) {
        const groupValues = [...new Set(data.rows.map(row => String(row[groupSeriesBy] ?? '')))]
        xLabels = [...new Set(data.rows.map(row => String(row[xAxis] ?? '')))];

        series = groupValues.flatMap(groupVal =>
            yAxes.map(yCol => {
                const rowsByX = new Map(
                    data.rows
                        .filter(row => String(row[groupSeriesBy] ?? '') === groupVal)
                        .map(row => [String(row[xAxis] ?? ''), row])
                );

                return {
                    label: groupValues.length === 1 ? yCol : `${groupVal}${yAxes.length > 1 ? ` - ${yCol}` : ''}`,
                    data: xLabels.map(x => {
                        const row = rowsByX.get(x);
                        if (!row) return null;
                        const val = row[yCol];
                        return val === null || val === undefined ? null : Number(val);
                    }),
                    ...(stacked ? { stack: 'total' } : {}),
                };
            })
        );
    } else {
        xLabels = data.rows.map(row => String(row[xAxis] ?? ''));
        series = yAxes.map(col => ({
            label: col,
            data: data.rows.map(row => {
                const val = row[col];
                return val === null || val === undefined ? null : Number(val);
            }),
            ...(stacked ? { stack: 'total' } : {}),
        }));
    }

    const axisData = [{ scaleType: 'band' as const, data: xLabels }];

    return (
        <BarChart
            xAxis={layout === 'vertical' ? axisData : undefined}
            yAxis={layout === 'horizontal' ? axisData : undefined}
            series={series}
            layout={layout}
            hideLegend={!showLegend}
            sx={{ width: '100%', height: '100%' }}
        />
    );
}